import { useState, useEffect } from "react";
import { View, Text, ScrollView, TextInput, TouchableOpacity, StyleSheet, Platform, Linking, ActivityIndicator } from "react-native";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { PublicNav } from "../../../components/PublicNav";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";
const API = "https://dashhound.app/api";

type Ev = {
  id: string; slug?: string; name: string; club: string; date: string; city: string; state: string;
  entry_url?: string; distance_miles?: number; distanceMiles?: number;
};

const RADII = [25, 50, 100, 250];

function fmtDate(d: string) {
  const dt = new Date(String(d).replace(" ", "T"));
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export default function FastCatEvents() {
  const router = useRouter();
  const [zip, setZip] = useState("");
  const [radius, setRadius] = useState(100);
  const [events, setEvents] = useState<Ev[]>([]);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  // Restore the previous search so returning from an event doesn't force a re-search.
  useEffect(() => {
    if (Platform.OS !== "web" || typeof sessionStorage === "undefined") return;
    try {
      const saved = sessionStorage.getItem("fce_search");
      if (saved) {
        const { qs, r, zip: z } = JSON.parse(saved);
        if (z) setZip(z);
        if (r) setRadius(r);
        if (qs) run(qs, r || radius);
      }
    } catch { /* ignore */ }
  }, []);

  async function run(qs: string, r: number = radius) {
    setLoading(true); setError(""); setSearched(true);
    if (Platform.OS === "web" && typeof sessionStorage !== "undefined") {
      try { sessionStorage.setItem("fce_search", JSON.stringify({ qs, r, zip })); } catch { /* ignore */ }
    }
    try {
      const res = await fetch(`${API}/events?${qs}&radius=${r}`);
      const data = await res.json();
      // A club that runs multiple Fast CAT trials in a day shows up once per AKC
      // event id. Collapse to one card per club per day (matches the app).
      const seen = new Set<string>();
      const deduped = (Array.isArray(data) ? data : []).filter((e: Ev) => {
        const key = `${e.club || e.name}-${e.date}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setEvents(deduped);
    } catch {
      setError("Something went wrong — try again.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  function searchZip() {
    const z = zip.replace(/[^0-9]/g, "").slice(0, 5);
    if (z.length !== 5) { setError("Enter a 5-digit ZIP code."); return; }
    run(`zip=${z}`);
  }

  function useMyLocation() {
    if (Platform.OS !== "web" || typeof navigator === "undefined" || !navigator.geolocation) {
      setError("Location isn't available here — enter a ZIP instead.");
      return;
    }
    setLocating(true); setError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLocating(false); run(`lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`); },
      (err) => {
        setLocating(false);
        setError(err && err.code === 1
          ? "Location is blocked — tap the 📍/lock icon in your address bar to allow it, or enter a ZIP."
          : "Couldn't get your location — enter a ZIP instead.");
      },
      { timeout: 15000, maximumAge: 60000, enableHighAccuracy: false }
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {Platform.OS === "web" && (
        <Head>
          <title>Fast CAT Events Near Me — Find AKC Fast CAT Events — Dash-Hound</title>
          <meta name="description" content="Find AKC Fast CAT events near you. Search by ZIP code or use your location to see upcoming 100-yard dash events, dates, locations, and how to enter — no account needed." />
          <meta property="og:title" content="Fast CAT Events Near Me — Dash-Hound" />
          <meta property="og:description" content="Search upcoming AKC Fast CAT events by ZIP or your location. Dates, locations, and entry links." />
          <meta property="og:image" content="https://dashhound.app/pages/fast-cat-events.jpg" />
          <meta property="og:url" content="https://dashhound.app/fast-cat-events" />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Fast CAT Events Near Me — Dash-Hound" />
          <meta name="twitter:image" content="https://dashhound.app/pages/fast-cat-events.jpg" />
        </Head>
      )}

      <PublicNav />

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <Text style={styles.heroKicker}>EVENTS</Text>
          <Text style={styles.heroTitle}>Find a Fast CAT Event Near You</Text>
          <Text style={styles.heroSub}>
            Enter your ZIP code or use your location to find upcoming AKC Fast CAT events — dates, locations, and how to enter.
          </Text>
        </View>

        <View style={styles.searchCard}>
          <View style={styles.searchRow}>
            <TextInput value={zip} onChangeText={setZip} placeholder="ZIP code" placeholderTextColor="#9CA3AF" keyboardType="number-pad" maxLength={5} style={styles.zipInput} onSubmitEditing={searchZip} />
            <TouchableOpacity style={styles.searchBtn} onPress={searchZip}><Text style={styles.searchBtnText}>Search</Text></TouchableOpacity>
          </View>
          {Platform.OS === "web" && (
            <TouchableOpacity style={styles.locBtn} onPress={useMyLocation} disabled={locating}>
              <Text style={styles.locBtnText}>{locating ? "Locating…" : "📍 Use my location"}</Text>
            </TouchableOpacity>
          )}
          <View style={styles.radiusRow}>
            <Text style={styles.radiusLabel}>Within</Text>
            {RADII.map((r) => (
              <TouchableOpacity key={r} onPress={() => setRadius(r)} style={[styles.radiusChip, r === radius && styles.radiusChipActive]}>
                <Text style={[styles.radiusChipText, r === radius && styles.radiusChipTextActive]}>{r} mi</Text>
              </TouchableOpacity>
            ))}
          </View>
          {error ? <Text style={styles.error}>{error}</Text> : null}
        </View>

        <View style={styles.results}>
          {loading ? (
            <View style={styles.loading}><ActivityIndicator color={NAVY} /></View>
          ) : !searched ? (
            <Text style={styles.hint}>Search above to see events near you.</Text>
          ) : events.length === 0 ? (
            <Text style={styles.hint}>No upcoming events within {radius} miles. Try a larger radius.</Text>
          ) : (
            <>
              <Text style={styles.resultCount}>{events.length} upcoming event{events.length === 1 ? "" : "s"}</Text>
              {events.map((e) => {
                const dist = e.distance_miles ?? e.distanceMiles;
                return (
                  <TouchableOpacity key={e.id} style={styles.eventCard} activeOpacity={0.7}
                    onPress={() => router.push(`/(auth)/fast-cat-events/${e.slug || e.id}` as any)}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.evDate}>{fmtDate(e.date)}{dist != null ? `  ·  ${Math.round(dist)} mi away` : ""}</Text>
                      <Text style={styles.evName}>{e.name || e.club}</Text>
                      <Text style={styles.evLoc}>{[e.city, e.state].filter(Boolean).join(", ")}</Text>
                      <View style={styles.evActions}>
                        <View style={styles.evDetails}><Text style={styles.evDetailsText}>View details →</Text></View>
                        {e.entry_url ? (
                          <TouchableOpacity style={styles.evEnter} onPress={() => Linking.openURL(e.entry_url!)}>
                            <Text style={styles.evEnterText}>Enter →</Text>
                          </TouchableOpacity>
                        ) : null}
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </>
          )}
        </View>

        <View style={styles.cta}>
          <Text style={styles.ctaTitle}>New to Fast CAT?</Text>
          <Text style={styles.ctaText}>Learn how the sport works and how to enter your first event — then track every run in the Dash-Hound app.</Text>
          <View style={{ flexDirection: "row", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
            <TouchableOpacity style={styles.ctaPrimary} onPress={() => router.push("/(auth)/what-is-fast-cat/" as any)}><Text style={styles.ctaPrimaryText}>What Is Fast CAT? →</Text></TouchableOpacity>
            <TouchableOpacity style={styles.ctaSecondary} onPress={() => router.push("/(auth)/how-to-signup/" as any)}><Text style={styles.ctaSecondaryText}>Sign-up Guide</Text></TouchableOpacity>
          </View>
        </View>

        <View style={styles.footer}><Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text></View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  content: { flexGrow: 1 },
  hero: { backgroundColor: NAVY, paddingHorizontal: 24, paddingTop: 40, paddingBottom: 32, alignItems: "center" },
  heroKicker: { color: ORANGE, fontSize: 12, fontWeight: "800", letterSpacing: 1.5, marginBottom: 10 },
  heroTitle: { color: WHITE, fontSize: 32, fontWeight: "900", textAlign: "center", letterSpacing: -0.5, maxWidth: 640 },
  heroSub: { color: "rgba(255,255,255,0.75)", fontSize: 16, textAlign: "center", marginTop: 12, maxWidth: 560, lineHeight: 24 },
  searchCard: { maxWidth: 560, width: "100%", alignSelf: "center", marginTop: -20, marginHorizontal: 16, padding: 20, backgroundColor: WHITE, borderRadius: 16, borderWidth: 1, borderColor: "#E5E7EB", ...(Platform.OS === "web" ? ({ boxShadow: "0 8px 24px rgba(27,42,74,0.10)" } as any) : { shadowColor: NAVY, shadowOpacity: 0.1, shadowRadius: 16, shadowOffset: { width: 0, height: 8 }, elevation: 6 }) },
  searchRow: { flexDirection: "row", gap: 10 },
  zipInput: { flex: 1, borderWidth: 1, borderColor: "#E5E7EB", borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 16, color: TEXT, ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : {}) },
  searchBtn: { backgroundColor: NAVY, paddingHorizontal: 22, justifyContent: "center", borderRadius: 10 },
  searchBtnText: { color: WHITE, fontWeight: "800", fontSize: 15 },
  locBtn: { marginTop: 10, paddingVertical: 11, alignItems: "center", borderRadius: 10, borderWidth: 1, borderColor: "#E5E7EB", backgroundColor: "#F9FAFB" },
  locBtnText: { color: NAVY, fontWeight: "700", fontSize: 14 },
  radiusRow: { flexDirection: "row", alignItems: "center", gap: 8, marginTop: 14, flexWrap: "wrap" },
  radiusLabel: { color: "#6B7280", fontSize: 13, fontWeight: "600" },
  radiusChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: "#F3F4F6", borderWidth: 1, borderColor: "#E5E7EB" },
  radiusChipActive: { backgroundColor: ORANGE, borderColor: ORANGE },
  radiusChipText: { color: NAVY, fontWeight: "700", fontSize: 13 },
  radiusChipTextActive: { color: NAVY },
  error: { color: "#DC2626", fontSize: 13, marginTop: 12 },
  results: { maxWidth: 560, width: "100%", alignSelf: "center", paddingHorizontal: 16, paddingTop: 24 },
  loading: { paddingVertical: 40, alignItems: "center" },
  hint: { textAlign: "center", color: "#6B7280", paddingVertical: 32, fontSize: 15 },
  resultCount: { fontSize: 13, fontWeight: "700", color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 },
  eventCard: { flexDirection: "row", padding: 16, backgroundColor: WHITE, borderRadius: 12, borderWidth: 1, borderColor: "#E5E7EB", marginBottom: 12 },
  evDate: { color: ORANGE, fontSize: 13, fontWeight: "800", marginBottom: 4 },
  evName: { color: NAVY, fontSize: 17, fontWeight: "800", lineHeight: 22 },
  evLoc: { color: "#6B7280", fontSize: 14, marginTop: 2 },
  evActions: { flexDirection: "row", gap: 10, marginTop: 12, alignItems: "center" },
  evEnter: { backgroundColor: NAVY, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  evEnterText: { color: WHITE, fontWeight: "700", fontSize: 13 },
  evDetails: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, borderWidth: 1, borderColor: "#D1D5DB" },
  evDetailsText: { color: NAVY, fontWeight: "700", fontSize: 13 },
  cta: { maxWidth: 560, width: "100%", alignSelf: "center", marginTop: 24, marginHorizontal: 16, padding: 22, backgroundColor: "#F9FAFB", borderRadius: 14, borderWidth: 1, borderColor: "#E5E7EB" },
  ctaTitle: { fontSize: 20, fontWeight: "800", color: NAVY, marginBottom: 8 },
  ctaText: { fontSize: 15, color: TEXT, lineHeight: 22 },
  ctaPrimary: { backgroundColor: NAVY, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaPrimaryText: { color: WHITE, fontWeight: "700", fontSize: 14 },
  ctaSecondary: { backgroundColor: ORANGE, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaSecondaryText: { color: NAVY, fontWeight: "800", fontSize: 14 },
  footer: { backgroundColor: NAVY_DARK, paddingVertical: 32, alignItems: "center", marginTop: 40 },
  footerText: { color: "rgba(255,255,255,0.35)", fontSize: 13 },
});
